//third-party
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import { At, KeyFill, CheckCircleFill, GeoAltFill } from "react-bootstrap-icons";
import { Alert, Card, Form, Modal } from "react-bootstrap";
import { showMoney } from "./Data/Money";

//spotsync
import { Api } from "./Data/Api";
import { Input } from "./Controls/Input";
import { Button } from "./Controls/Button";
import { CenteredPage } from "./Controls/CenteredPage";
import { HeaderText } from "./Controls/HeaderText";
import { useAtom } from "jotai";
import { authAtom, authTokenAtom, redirectAtom } from "./Data/Atoms";
import { Parker } from "./Data/ApiTypes";
import { parseParker } from "./Data/ApiParse";
import { validatePassword } from "./Data/Validation";
import { showDate } from "./Data/Date";
import { toast } from "react-toastify";
import { loginAndRedirect } from "./Data/AuthUtils";

interface ResetPasswordForm {
    emailAddress: string;
    password: string;
    confirmPassword: string;
}

export const ResetPasswordPage = () => {
    const nav = useNavigate();
    const [searchParams, _] = useSearchParams();
    const resetCode = searchParams.get('code')!;
    const inviteCode = searchParams.get('invite');
    const [auth, setAuth] = useAtom(authAtom);
    const [msg, setMsg] = useState<string>();
    const [redirect, setRedirect] = useAtom(redirectAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<any>(null);
    const [inviteData, setInviteData] = useState<any>(null);
    const parker = useLoaderData() as Parker;
    const [showModal, setShowModal] = useState(false);
    const [authToken, setAuthToken] = useAtom(authTokenAtom);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
        setError,
    } = useForm<ResetPasswordForm>({ defaultValues: { emailAddress: parker.emailAddress } });

    useEffect(() => {
        if (inviteCode) {
            const parkerPlanInviteId = parseInt(inviteCode);
            Api.planInviteGet(parkerPlanInviteId)
                .then(async res => {
                    if (res.isOk) {
                        const inv = res.value;
                        const facility = await Api.facilityDetails(inv.facilityId)
                            .then(res => res.isOk ? res.value : null);
                        if (facility) {
                            setInviteData({
                                isInvite: true,
                                inviteId: parkerPlanInviteId,
                                invite: inv,
                                facility: facility,
                                quantity: inv.quantity
                            });
                            setSelectedPlan({ ...inv.listedPlan, price: inv.privatePrice });
                            setShowModal(true);
                        }
                    }
                });
        }
    }, [inviteCode]);

    function submit(data: ResetPasswordForm) {
        const validation = validatePassword(data.password);
        if (!validation.isValid) {
            validation.errors.forEach(error => {
                setError('password', { type: 'manual', message: error });
            });
            return;
        }

        // Clear localStorage BEFORE making any API calls
        localStorage.clear();
        
        setIsLoading(true);
        Api.resetPassword({
            resetPasswordCode: resetCode!,
            newPassword: data.password
        }).then(res => {
            if (res.isOk) {
                const parker = parseParker(res.value);
                toast.info('Password changed successfuly');
                
                return loginAndRedirect({
                    emailAddress: parker.emailAddress,
                    password: data.password,
                    parker,
                    setAuthToken,
                    setAuth,
                    redirect,
                    setRedirect,
                    nav
                });
            }
            setMsg(res.error);
            setIsLoading(false);
        });
    }

    return <CenteredPage style={{ maxWidth: "30em" }}>
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>Parking Plan Invitation</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {inviteData && <>
                    <div className="mb-2 d-flex gap-3 align-items-start">
                        <CheckCircleFill className="flex-shrink-0 mt-2" style={{ fontSize: "1.5rem", color: "#FF4DB8" }} />
                        <p>You've been invited to park at <span className="fw-bold">{inviteData.facility.name}</span> for the {selectedPlan.name} starting on <span className="fw-bold">{showDate(inviteData.invite.startDate)}</span>.</p>
                    </div>
                    <div className="mb-4 d-flexalign-items-start">
                        <p className="text-secondary fs-7">Click Continue to complete your account registration.</p>
                    </div>
                    <div className="fs-4 fw-bold mb-2">{inviteData.facility.name}</div>
                    <div className="text-secondary mb-3 d-flex gap-2 align-items-start">
                        <GeoAltFill className="flex-shrink-0" style={{ fontSize: "1.2rem", marginTop: "0.2rem", color: "#6c757d" }} />
                        <div>
                            {inviteData.facility.address.addressLine1}
                            {inviteData.facility.address.addressLine2 && <>, {inviteData.facility.address.addressLine2}</>}
                            <br />
                            {inviteData.facility.address.city}, {inviteData.facility.address.state} {inviteData.facility.address.zipCode}
                        </div>
                    </div>
                    <div className="fs-5 fw-bold">
                        Monthly Rate: {showMoney(inviteData.invite.privatePrice)}
                    </div>
                </>}
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={() => nav('/')}>No, thanks</Button>
                <Button className="btn-main" onClick={() => setShowModal(false)}>Continue</Button>
            </Modal.Footer>
        </Modal>

        <Card className="w-100">
            <Form onSubmit={handleSubmit(submit)}>
                <Card.Header>
                    <HeaderText>Reset My Password</HeaderText>
                </Card.Header>
                <Card.Body>
                    <div className="text-secondary mb-3">
                        Reset your password below.
                    </div>
                    <Input
                        type="email"
                        readOnly
                        label="Email Address"
                        icon={<At className="fs-5" />}
                        placeholder="Enter your email address"
                        error={errors.emailAddress}
                        {...register("emailAddress", { required: "Email Address is required" })}
                    />
                    <Input type="password"
                        label="New Password"
                        icon={<KeyFill className="fs-5" />}
                        error={errors.password}
                        {...register("password", {
                            required: "Password is required",
                            validate: (value) => {
                                const validation = validatePassword(value);
                                return validation.isValid || validation.errors[0];
                            }
                        })}
                    />
                    <Input type="password"
                        label="Confirm Password"
                        icon={<KeyFill className="fs-5" />}
                        error={errors.confirmPassword}
                        {...register("confirmPassword", {
                            required: "Please confirm your password",
                            validate: value => value === watch('password') || "Passwords do not match"
                        })}
                    />
                    {msg && <Alert variant="secondary">{msg}</Alert>}
                </Card.Body>
                <Card.Footer>
                    <div className="d-flex flex-row justify-content-end">
                        <Button type="submit" loading={isLoading} className="w-50 btn-main">
                            Reset Password
                        </Button>
                    </div>
                </Card.Footer>
            </Form>
        </Card>
    </CenteredPage>;
};